import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldIcon, ClockIcon, CheckCircleIcon, MapPinIcon, GlobeIcon, AwardIcon, LockIcon, MailIcon, ChevronDown, ArrowRightIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Select } from '../components/ui/Select';
type Language = 'en' | 'es';
const content = {
  en: {
    nav: {
      email: 'medicalhydrate@gmail.com'
    },
    hero: {
      headline: 'Private Medical Care in the Heart of New York',
      subheadline: 'Board-certified physicians. Appointment-based consultations. Complete confidentiality.',
      cta: 'Request a Consultation',
      badges: ['Board Certified', 'NY Licensed', 'English & Spanish', 'Central Park South'],
      imageCaption: '116 Central Park South, New York'
    },
    about: {
      headline: 'A Different Kind of Medical Experience',
      body: 'Hydrate Medical, PLLC is a New York professional medical practice offering private consultations by scheduled appointment only. All procedures are performed by board-certified physicians licensed to practice medicine in the State of New York. Your treating physician will be identified during your preoperative consultation.',
      credentials: ['Board-Certified Physicians', 'New York Licensed', 'Patient Confidentiality']
    },
    process: {
      title: 'How It Works',
      steps: [{
        title: 'Submit Request',
        desc: 'Provide your basic information securely.'
      }, {
        title: 'Coordinator Contacts You',
        desc: 'We reach out to understand your needs.'
      }, {
        title: 'Appointment Confirmed',
        desc: 'A dedicated time is reserved for you.'
      }, {
        title: 'Private Consultation',
        desc: 'Meet with your board-certified physician.'
      }]
    },
    legal: {
      title: 'Professional Medical Practice',
      entity: 'Hydrate Medical, PLLC — NY Professional Limited Liability Company',
      text: 'All procedures are performed by board-certified physicians licensed to practice medicine in the State of New York. Your treating physician will be identified during your preoperative consultation.'
    },
    form: {
      title: 'Request Your Private Consultation',
      subtitle: 'A medical coordinator will contact you within 24 hours to schedule your appointment.',
      fields: {
        name: 'Full Name',
        phone: 'Phone',
        language: 'Preferred Language',
        service: 'Service of Interest',
        message: 'Message'
      },
      cta: 'Submit Request',
      disclaimer: 'Submitting this form does not confirm an appointment. A coordinator will contact you to schedule.',
      trust: ['24h Response', '100% Confidential', 'No Obligation'],
      success: {
        title: 'Request Received',
        message: 'Thank you. A medical coordinator will reach out to you within 24 hours.',
        button: 'Submit Another Request'
      }
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [{
        q: 'How do I schedule an appointment?',
        a: 'You can request a consultation through our secure form. Our medical coordinator will contact you to arrange a suitable time.'
      }, {
        q: 'What services are offered?',
        a: 'We offer private, appointment-based medical consultations and treatments by board-certified physicians.'
      }, {
        q: 'Do you accept insurance?',
        a: 'We are an out-of-network provider. We can provide necessary documentation for you to submit to your insurance for potential reimbursement.'
      }, {
        q: 'How is my confidentiality protected?',
        a: 'We maintain strict HIPAA compliance and offer a highly private environment with staggered appointments to ensure maximum discretion.'
      }, {
        q: 'What languages do you speak?',
        a: 'Our staff and physicians are fully bilingual in English and Spanish.'
      }, {
        q: 'Is there parking available?',
        a: 'There are several premium parking garages located immediately adjacent to our Central Park South location.'
      }]
    }
  },
  es: {
    nav: {
      email: 'medicalhydrate@gmail.com'
    },
    hero: {
      headline: 'Atención Médica Privada en el Corazón de Nueva York',
      subheadline: 'Médicos certificados. Consultas con cita previa. Confidencialidad total.',
      cta: 'Solicitar Consulta',
      badges: ['Médicos Certificados', 'Licencia de NY', 'Inglés y Español', 'Central Park Sur'],
      imageCaption: '116 Central Park Sur, Nueva York'
    },
    about: {
      headline: 'Una Experiencia Médica Diferente',
      body: 'Hydrate Medical, PLLC es una práctica médica profesional en Nueva York que ofrece consultas privadas únicamente con cita previa. Todos los procedimientos son realizados por médicos certificados con licencia para ejercer la medicina en el Estado de Nueva York. Su médico tratante será identificado durante su consulta preoperatoria.',
      credentials: ['Médicos Certificados', 'Licencia de Nueva York', 'Confidencialidad del Paciente']
    },
    process: {
      title: 'Cómo Funciona',
      steps: [{
        title: 'Enviar Solicitud',
        desc: 'Proporcione su información básica de forma segura.'
      }, {
        title: 'El Coordinador le Contacta',
        desc: 'Nos comunicamos para entender sus necesidades.'
      }, {
        title: 'Cita Confirmada',
        desc: 'Se reserva un tiempo dedicado para usted.'
      }, {
        title: 'Consulta Privada',
        desc: 'Reúnase con su médico certificado.'
      }]
    },
    legal: {
      title: 'Práctica Médica Profesional',
      entity: 'Hydrate Medical, PLLC — NY Professional Limited Liability Company',
      text: 'Todos los procedimientos son realizados por médicos certificados con licencia para ejercer la medicina en el Estado de Nueva York. Su médico tratante será identificado durante su consulta preoperatoria.'
    },
    form: {
      title: 'Solicite Su Consulta Privada',
      subtitle: 'Un coordinador médico le contactará dentro de las 24 horas para programar su cita.',
      fields: {
        name: 'Nombre Completo',
        phone: 'Teléfono',
        language: 'Idioma Preferido',
        service: 'Servicio de Interés',
        message: 'Mensaje'
      },
      cta: 'Enviar Solicitud',
      disclaimer: 'Enviar este formulario no confirma una cita. Un coordinador le contactará para programar.',
      trust: ['Respuesta en 24h', '100% Confidencial', 'Sin Compromiso'],
      success: {
        title: 'Solicitud Recibida',
        message: 'Gracias. Un coordinador médico se comunicará con usted dentro de las 24 horas.',
        button: 'Enviar Otra Solicitud'
      }
    },
    faq: {
      title: 'Preguntas Frecuentes',
      items: [{
        q: '¿Cómo programo una cita?',
        a: 'Puede solicitar una consulta a través de nuestro formulario seguro. Nuestro coordinador médico se comunicará con usted para acordar un horario adecuado.'
      }, {
        q: '¿Qué servicios ofrecen?',
        a: 'Ofrecemos consultas y tratamientos médicos privados con cita previa, realizados por médicos certificados.'
      }, {
        q: '¿Aceptan seguro médico?',
        a: 'Somos un proveedor fuera de la red. Podemos proporcionar la documentación necesaria para que la envíe a su seguro para un posible reembolso.'
      }, {
        q: '¿Cómo se protege mi confidencialidad?',
        a: 'Mantenemos un estricto cumplimiento de HIPAA y ofrecemos un entorno altamente privado con citas escalonadas para garantizar la máxima discreción.'
      }, {
        q: '¿Qué idiomas hablan?',
        a: 'Nuestro personal y médicos son completamente bilingües en inglés y español.'
      }, {
        q: '¿Hay estacionamiento disponible?',
        a: 'Hay varios estacionamientos premium ubicados inmediatamente adyacentes a nuestra ubicación en Central Park Sur.'
      }]
    }
  }
};
// Animation Variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const scaleIn = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
export function HydrateLanding() {
  const [lang, setLang] = useState<Language>('en');
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const t = content[lang];
  const [formState, setFormState] = useState({
    name: '',
    phone: '',
    language: 'english',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormState({
        name: '',
        phone: '',
        language: 'english',
        service: '',
        message: ''
      });
    }, 1500);
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleLanguage = () => {
    setLang(lang === 'en' ? 'es' : 'en');
  };
  return <div className="min-h-screen bg-navy-900 text-white font-sans selection:bg-gold selection:text-navy-900" data-id="element-21">
      {/* STICKY NAVBAR */}
      <header className={`fixed top-0 left-0 right-0 z-50 py-4 px-6 md:px-12 flex justify-between items-center transition-all duration-300 ${isScrolled ? 'bg-navy-900/90 backdrop-blur-md border-b border-gold/20 py-3' : 'bg-transparent'}`} data-id="element-22">
        <div className="w-1/3 flex justify-start" data-id="element-23">
          <div className="text-lg md:text-xl font-medium tracking-widest text-white" data-id="element-24">
            HYDRATE MEDICAL<span className="text-gold" data-id="element-25">.</span>
          </div>
        </div>

        <div className="w-1/3 flex justify-center" data-id="element-26">
          <button onClick={toggleLanguage} className="text-xs tracking-widest uppercase flex items-center bg-navy-800/80 px-4 py-2 rounded-full border border-gold/30 shadow-sm hover:border-gold transition-colors" aria-label="Toggle language" data-id="element-27">
            <span className={lang === 'en' ? 'text-gold font-semibold' : 'text-gray-400'} data-id="element-28">
              EN
            </span>
            <span className="mx-3 text-gray-600" data-id="element-29">|</span>
            <span className={lang === 'es' ? 'text-gold font-semibold' : 'text-gray-400'} data-id="element-30">
              ES
            </span>
          </button>
        </div>

        <div className="w-1/3 flex justify-end" data-id="element-31">
          <a href={`mailto:${t.nav.email}`} className="text-white hover:text-gold transition-colors p-2 rounded-full hover:bg-gold/10" aria-label="Contact via Email" data-id="element-32">
            <MailIcon className="w-5 h-5" data-id="element-33" />
          </a>
        </div>
      </header>

      {/* 1. HERO SECTION */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden bg-gradient-to-b from-navy-800 via-navy-700 to-navy-900" data-id="element-34">
        {/* Abstract background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-35">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-gold/5 blur-[120px]" data-id="element-36" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-navy-700/50 blur-[100px]" data-id="element-37" />
        </div>

        <div className="max-w-7xl mx-auto px-6 w-full relative z-10" data-id="element-38">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-39">
            {/* Left Column: Content */}
            <motion.div className="lg:col-span-7 flex flex-col items-start" initial="hidden" animate="visible" variants={staggerContainer} data-id="element-40">
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3 mb-8 text-[10px] md:text-xs uppercase tracking-widest text-gold" data-id="element-41">
                <span className="flex items-center bg-navy-800/50 px-3 py-1.5 rounded-full border border-gold/20" data-id="element-42">
                  <ShieldIcon className="w-3 h-3 mr-2" data-id="element-43" /> {t.hero.badges[0]}
                </span>
                <span className="flex items-center bg-navy-800/50 px-3 py-1.5 rounded-full border border-gold/20" data-id="element-44">
                  <AwardIcon className="w-3 h-3 mr-2" data-id="element-45" /> {t.hero.badges[1]}
                </span>
                <span className="flex items-center bg-navy-800/50 px-3 py-1.5 rounded-full border border-gold/20" data-id="element-46">
                  <GlobeIcon className="w-3 h-3 mr-2" data-id="element-47" /> {t.hero.badges[2]}
                </span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.1] mb-6 text-white tracking-tight" data-id="element-48">
                {t.hero.headline}
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-gray-300 font-light max-w-xl mb-10 leading-relaxed" data-id="element-49">
                {t.hero.subheadline}
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center gap-6 w-full sm:w-auto" data-id="element-50">
                <Button onClick={scrollToForm} className="w-full sm:w-auto py-5 px-10 text-base" data-id="element-51">
                  {t.hero.cta}
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="mt-12 flex items-center text-xs tracking-widest uppercase text-gray-400" data-id="element-52">
                <MapPinIcon className="w-4 h-4 mr-2 text-gold" data-id="element-53" />
                {t.hero.badges[3]}
              </motion.div>
            </motion.div>

            {/* Right Column: Photo Card */}
            <motion.div className="lg:col-span-5 relative mt-12 lg:mt-0" initial="hidden" animate="visible" variants={scaleIn} data-id="element-54">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-2 border-gold/30 ring-1 ring-gold/20 aspect-[4/5] max-w-md mx-auto lg:ml-auto bg-gray-900 group" data-id="element-55">
                <img src="https://cdn.magicpatterns.com/uploads/pF2UJJNcY4VGBZpZbVfvjr/Screenshot_2026-03-24_at_5.49.24_PM.png" alt="Private Waiting Lounge" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 opacity-90" data-id="element-56" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/20 to-transparent" data-id="element-57" />

                <div className="absolute bottom-0 left-0 w-full p-8" data-id="element-58">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-gold/20 backdrop-blur-md border border-gold/30 text-gold text-[10px] uppercase tracking-widest mb-3" data-id="element-59">
                    <MapPinIcon className="w-3 h-3 mr-1.5" data-id="element-60" /> Location
                  </div>
                  <p className="text-white text-lg font-medium tracking-wide" data-id="element-61">
                    {t.hero.imageCaption}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. ABOUT THE PRACTICE */}
      <section className="py-24 md:py-32 px-6 bg-white text-charcoal" data-id="element-62">
        <motion.div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} data-id="element-63">
          <motion.div variants={fadeInUp} data-id="element-64">
            <h2 className="text-3xl md:text-5xl font-semibold mb-8 tracking-tight" data-id="element-65">
              {t.about.headline}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed font-light" data-id="element-66">
              {t.about.body}
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} className="space-y-6" data-id="element-67">
            {[{
            icon: AwardIcon,
            text: t.about.credentials[0]
          }, {
            icon: CheckCircleIcon,
            text: t.about.credentials[1]
          }, {
            icon: LockIcon,
            text: t.about.credentials[2]
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="flex items-center p-6 bg-cream rounded-2xl border border-gold/20 shadow-sm hover:shadow-md transition-shadow" data-id="element-68">
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mr-6 shadow-sm border border-gold/10 flex-shrink-0" data-id="element-69">
                  <item.icon className="w-6 h-6 text-gold" data-id="element-70" />
                </div>
                <h3 className="text-xl font-medium" data-id="element-71">{item.text}</h3>
              </motion.div>)}
          </motion.div>
        </motion.div>
      </section>

      {/* 3. HOW IT WORKS */}
      <section className="py-24 md:py-32 px-6 bg-navy-900 relative border-y border-gold/10" data-id="element-72">
        <div className="max-w-7xl mx-auto" data-id="element-73">
          <motion.div className="text-center mb-20" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-74">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white" data-id="element-75">
              {t.process.title}
            </h2>
          </motion.div>

          <motion.div className="relative" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-76">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-[1px] bg-gold/30 z-0" data-id="element-77" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10" data-id="element-78">
              {t.process.steps.map((step, index) => <motion.div key={index} variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-79">
                  <div className="w-20 h-20 rounded-full bg-navy-800 border-2 border-gold text-gold flex items-center justify-center text-2xl font-light mb-6 shadow-[0_0_30px_rgba(201,160,132,0.15)]" data-id="element-80">
                    0{index + 1}
                  </div>
                  <h3 className="text-xl font-medium mb-3 text-white" data-id="element-81">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 font-light leading-relaxed" data-id="element-82">
                    {step.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. LEGAL SECTION */}
      <section className="py-16 px-6 bg-navy-800 text-center" data-id="element-83">
        <motion.div className="max-w-4xl mx-auto" initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} data-id="element-84">
          <ShieldIcon className="w-8 h-8 text-gold/50 mx-auto mb-6" data-id="element-85" />
          <h3 className="text-sm tracking-widest uppercase text-gold mb-4" data-id="element-86">
            {t.legal.title}
          </h3>
          <p className="text-gray-300 font-light leading-relaxed mb-4" data-id="element-87">
            {t.legal.text}
          </p>
          <p className="text-xs text-gray-500 uppercase tracking-widest" data-id="element-88">
            {t.legal.entity}
          </p>
        </motion.div>
      </section>

      {/* 5. CONSULTATION FORM */}
      <section ref={formRef} className="py-24 md:py-32 px-6 bg-cream text-charcoal" data-id="element-89">
        <div className="max-w-7xl mx-auto" data-id="element-90">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start" data-id="element-91">
            <motion.div className="lg:col-span-5" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-92">
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-semibold mb-6 tracking-tight" data-id="element-93">
                {t.form.title}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-gray-600 font-light mb-12 leading-relaxed" data-id="element-94">
                {t.form.subtitle}
              </motion.p>

              <motion.div variants={staggerContainer} className="space-y-6 mb-12" data-id="element-95">
                {[{
                icon: ClockIcon,
                text: t.form.trust[0]
              }, {
                icon: LockIcon,
                text: t.form.trust[1]
              }, {
                icon: ShieldIcon,
                text: t.form.trust[2]
              }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="flex items-center text-gray-700" data-id="element-96">
                    <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mr-4" data-id="element-97">
                      <item.icon className="w-5 h-5 text-gold" data-id="element-98" />
                    </div>
                    <span className="font-medium" data-id="element-99">{item.text}</span>
                  </motion.div>)}
              </motion.div>
            </motion.div>

            <motion.div className="lg:col-span-7" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={scaleIn} data-id="element-100">
              <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100" data-id="element-101">
                {isSubmitted ? <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="flex flex-col items-center justify-center text-center py-16" data-id="element-102">
                    <div className="w-24 h-24 rounded-full bg-lime-50 flex items-center justify-center mb-8" data-id="element-103">
                      <CheckCircleIcon className="w-12 h-12 text-lime-500" data-id="element-104" />
                    </div>
                    <h3 className="text-3xl font-semibold mb-4" data-id="element-105">
                      {t.form.success.title}
                    </h3>
                    <p className="text-gray-600 text-lg mb-10 max-w-md" data-id="element-106">
                      {t.form.success.message}
                    </p>
                    <Button onClick={() => setIsSubmitted(false)} variant="outline" data-id="element-107">
                      {t.form.success.button}
                    </Button>
                  </motion.div> : <form onSubmit={handleFormSubmit} className="space-y-6" data-id="element-108">
                    <Input label={`${t.form.fields.name} *`} required value={formState.name} onChange={e => setFormState({
                  ...formState,
                  name: e.target.value
                })} data-id="element-109" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-110">
                      <Input label={`${t.form.fields.phone} *`} type="tel" required value={formState.phone} onChange={e => setFormState({
                    ...formState,
                    phone: e.target.value
                  })} data-id="element-111" />
                      <Select label={t.form.fields.language} options={[{
                    value: 'english',
                    label: 'English'
                  }, {
                    value: 'spanish',
                    label: 'Español'
                  }]} value={formState.language} onChange={e => setFormState({
                    ...formState,
                    language: e.target.value
                  })} data-id="element-112" />
                    </div>

                    <Input label={t.form.fields.service} value={formState.service} onChange={e => setFormState({
                  ...formState,
                  service: e.target.value
                })} data-id="element-113" />

                    <Textarea label={`${t.form.fields.message} (Optional)`} value={formState.message} onChange={e => setFormState({
                  ...formState,
                  message: e.target.value
                })} data-id="element-114" />

                    <div className="pt-6" data-id="element-115">
                      <Button type="submit" fullWidth disabled={isSubmitting} className="py-5 text-base group" data-id="element-116">
                        {isSubmitting ? '...' : t.form.cta}
                        {!isSubmitting && <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-117" />}
                      </Button>
                    </div>

                    <p className="text-xs text-gray-500 text-center mt-6" data-id="element-118">
                      {t.form.disclaimer}
                    </p>
                  </form>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. FAQ SECTION */}
      <section className="py-24 md:py-32 px-6 bg-navy-800" data-id="element-119">
        <div className="max-w-3xl mx-auto" data-id="element-120">
          <motion.h2 className="text-3xl md:text-5xl font-semibold text-center mb-16 tracking-tight text-white" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-121">
            {t.faq.title}
          </motion.h2>

          <div className="space-y-4" data-id="element-122">
            {t.faq.items.map((faq, index) => <motion.div key={index} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="border border-gold/20 rounded-2xl bg-navy-900/50 overflow-hidden" data-id="element-123">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} className="w-full px-6 py-6 flex justify-between items-center text-left focus:outline-none" data-id="element-124">
                  <span className="text-lg font-medium text-white pr-8" data-id="element-125">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gold flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} data-id="element-126" />
                </button>
                <AnimatePresence data-id="element-127">
                  {openFaqIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-128">
                      <div className="px-6 pb-6 text-gray-400 font-light leading-relaxed" data-id="element-129">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 7. FOOTER */}
      <footer className="bg-navy-900 py-16 px-6 border-t border-gold/10" data-id="element-130">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center md:items-start gap-10 text-center md:text-left" data-id="element-131">
          <div data-id="element-132">
            <div className="text-xl font-medium tracking-widest text-white mb-6" data-id="element-133">
              HYDRATE MEDICAL<span className="text-gold" data-id="element-134">.</span>
            </div>
            <p className="text-sm text-gray-400 mb-2" data-id="element-135">116 Central Park South</p>
            <p className="text-sm text-gray-400 mb-6" data-id="element-136">New York, NY 10019</p>
            <a href={`mailto:${t.nav.email}`} className="text-gold hover:text-white transition-colors text-sm font-medium" data-id="element-137">
              {t.nav.email}
            </a>
          </div>

          <div className="flex flex-col items-center md:items-end justify-between h-full" data-id="element-138">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4 md:mb-8 max-w-xs text-center md:text-right leading-relaxed" data-id="element-139">
              Licensed to Practice Medicine in the State of New York
            </p>
            <p className="text-[10px] text-gold uppercase tracking-widest mb-4" data-id="element-140">
              NY Professional Limited Liability Company
            </p>
            <p className="text-[10px] text-gray-500 uppercase tracking-widest" data-id="element-141">
              © 2026 Hydrate Medical, PLLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}